import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useGateMainContext } from "../GateMainContext";
import { Alert, alpha, Box, Button, Checkbox, FormControlLabel, FormGroup, IconButton, Snackbar, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import './AdminScene.css';
import { AccountCircleOutlined, AddCircle, AddCircleOutlineOutlined, Close, DeleteOutline, EditOutlined, Microsoft, People, SettingsOutlined, ShareOutlined } from "@mui/icons-material";
import NoDataPlaceholder from "../Shared/NoDataPlaceholder";
import InfoDialog from "../Shared/InfoDialog";
import DeleteDialog from "../Shared/DeleteDialog";
import { RemoveUserFromTenantRequest, ResetPasswordUserRequest, SaveUserDataRequest, TenantDTO, TenantUserDTO, UsersGateApiClient } from "../Api/ApiServerHooks";
import { useGateApi } from "../Api/useGateApi";
import CreateTenantDialog from "./CreateTenantDialog";
import CreateUserDialog from "./CreateUserDialog";
import AddUserToTenantDialog from "./AddUserToTenantDialog";
import NewPasswordDialog from "./NewPasswordDialog";
import { useTranslation } from "react-i18next";
import LanguagePicker from "../Shared/LanguagePicker";
import PermissionSettingsDialog from "./PermissionSettingsDialog";
import PricingDatabaseSharingDialog from "./PricingDatabaseSharingDialog";
import PermissionsSelectComponent from "../Permissions/PermissionsSelectComponent";
import ImportUsersExcelDialog from "./ImportUsersExcelDialog";
import { Link } from "react-router-dom";
import OrganizationBranding from "./OrganizationBranding";

interface AdminSceneProps {
    isActualTenant: boolean;
}

export default function AdminScene(props: Readonly<AdminSceneProps>) {
    const { isActualTenant } = props;
    const theme = useTheme();
    const navigate = useNavigate();
    const { userData, refreshUserData } = useGateMainContext();
    const [tenants, setTenants] = useState<TenantDTO[]>();
    const [selectedTenant, setSelectedTenant] = useState<TenantDTO>();
    const [selectedUser, setSelectedUser] = useState<Partial<TenantUserDTO>>();
    const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
    const [userDialogOpen, setUserDialogOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [userCreateDialogOpen, setUserCreateDialogOpen] = useState(false);
    const [newPassDialogOpen, setNewPassDialogOpen] = useState(false);
    const [importUsersDialogOpen, setImportUsersDialogOpen] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [buttonResetDisabled, setButtonResetDisabled] = useState(false);
    const [saveConfirm, setSaveConfirm] = useState(false);
    const [newPass, setNewPass] = useState<string>('');
    const [emailCreate, setEmailCreate] = useState<string | undefined>(undefined);
    const [deletingTenant, setDeletingTenant] = useState<TenantDTO | null>(null);
    const [updatingTenant, setUpdatingTenant] = useState<TenantDTO | undefined>(undefined);
    const [removingUser, setRemovingUser] = useState<TenantUserDTO | null>(null);
    const { authorizedApi } = useGateApi(UsersGateApiClient);
    const [infoProps, setInfoProps] = useState<{ label: string, text: string }>();
    const { t } = useTranslation();

    // Admin manages every tenant; tenant manager only the tenant they are signed into, and never an admin
    // account. The password belongs to the whole account, so a tenant manager cannot reset it for an
    // account shared with other tenants. Mirrors TenantAccess on the server.
    const isAdmin = userData?.isAdmin === true;
    const isTenantManager = isAdmin || userData?.isTenantManager === true;
    const canEditSelectedUser = isAdmin || (isActualTenant && isTenantManager && selectedUser?.isAdmin !== true);
    const canResetSelectedPassword = isAdmin || (canEditSelectedUser && selectedUser?.belongsToOtherTenants !== true);

    useEffect(() => {
        if (userData && !(isActualTenant ? isTenantManager : isAdmin))
            navigate('/portal');
    }, [userData, isActualTenant, isTenantManager, isAdmin, navigate]);

    const refreshData = useCallback((tenId: number | undefined, userId: number | undefined) => {
        if (isActualTenant) {
            authorizedApi?.getUsersTenant().then((d) => {
                if (d.tenant) {
                    setTenants([d.tenant]);
                    setSelectedTenant(d.tenant);
                    if (userId && d.tenant.users)
                        setSelectedUser(d.tenant.users.find(u => u.id === userId));
                }
            });
        }
        else {
            authorizedApi?.getTenantsAdmin().then((d) => {
                setTenants(d.tenants);
                if (tenId && d.tenants) {
                    let tenant = d.tenants.find(t => t.id === tenId);
                    setSelectedTenant(tenant);
                    if (userId && tenant?.users)
                        setSelectedUser(tenant.users.find(u => u.id === userId));
                }
            });
        }
    }, [authorizedApi, isActualTenant]);

    useEffect(() => {
        refreshData(undefined, undefined);
        if (userData?.canCreateTenants || userData?.id === selectedUser?.id)
            setButtonDisabled(false);
        if (userData?.canCreateTenants)
            setButtonResetDisabled(false);
    }, [userData?.isTenantManager, userData?.canCreateTenants, userData?.id, userData?.tenantId, refreshData, selectedUser?.id, selectedTenant?.id]);

    function selectTenant(tenant: TenantDTO | undefined) {
        if (tenant === undefined) {
            setSelectedUser(undefined);
            setSelectedTenant(undefined);
        }
        else if (selectedTenant !== tenant) {
            setSelectedTenant(tenant);
            if (tenant.users && selectedUser && tenant.users.some(u => u.id === selectedUser.id)) {
                setSelectedUser(tenant.users.find(u => u.id === selectedUser.id));
            }
            else
                setSelectedUser(undefined);
        }
    }

    function saveUser() {
        if (selectedUser) {
            setButtonDisabled(true);
            authorizedApi?.saveUserData(new SaveUserDataRequest({ id: selectedUser.id, name: selectedUser.name, surname: selectedUser.surname, isAdmin: selectedUser.isAdmin, isTenantManager: selectedUser.isTenantManager, canCreateTenants: selectedUser.canCreateTenants, language: selectedUser.language, roleId: selectedUser.roleId, tenantId: selectedTenant?.id })).then((d) => {
                if (userData?.id === selectedUser?.id) {
                    refreshUserData();
                    if (isActualTenant ? (!selectedUser.isAdmin && !selectedUser.isTenantManager) : !selectedUser.isAdmin)
                        navigate('/portal');
                    else {
                        setButtonDisabled(false);
                        setSaveConfirm(true);
                    }
                }
                else {
                    refreshData(selectedTenant ? selectedTenant.id : undefined, selectedUser.id);
                    setButtonDisabled(false);
                    setSaveConfirm(true);
                }
            });
        }
    }

    function resetPassword() {
        if (selectedUser?.id) {
            setButtonResetDisabled(true);
            authorizedApi?.resetPasswordUser(new ResetPasswordUserRequest({
                id: selectedUser.id
            })).then((d) => {
                if (d.ok && d.newPassword) {
                    setNewPass(d.newPassword);
                    setNewPassDialogOpen(true);
                }
                setButtonResetDisabled(false);
            });
        }
    }
    const [tenantPermissionId, setTenantPermissionId] = useState<number | null>(null);
    const [sharingTenant, setSharingTenant] = useState<{ id: number; name?: string } | null>(null);
    const [adminView, setAdminView] = useState<'users' | 'branding'>('users');
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            <PermissionSettingsDialog isOpen={tenantPermissionId !== null} onClose={(refresh) => setTenantPermissionId(null)} tenantId={tenantPermissionId ?? 0} />
            <PricingDatabaseSharingDialog isOpen={sharingTenant !== null} onClose={() => setSharingTenant(null)} tenantId={sharingTenant?.id ?? 0} tenantName={sharingTenant?.name} />
            <Box sx={{ display: 'flex', position: 'relative', flexShrink: 0, width: '100%', backgroundColor: 'background.default', minHeight: '64px', zIndex: 1298, boxShadow: 'rgb(0 0 0 / 20%) 0px 6px 18px', borderBottom: '1px solid', borderColor: 'divider', boxSizing: 'border-box', px: { xs: 2, sm: 3 }, py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
                        {isActualTenant && <Typography variant='h5' color='primary' sx={{ fontWeight: 'bolder' }} >{t('UsersPopup.TenantAdministration')}</Typography>}
                        {!isActualTenant && <Typography variant='h5' color='primary' sx={{ fontWeight: 'bolder' }} >{t('UsersPopup.Admin')}</Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: { xs: 1, sm: 2 }, flex: '0 1 auto', flexWrap: 'wrap', ml: 'auto' }}>
                        {isActualTenant && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    variant={adminView === 'users' ? 'contained' : 'outlined'}
                                    onClick={() => setAdminView('users')}
                                    sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 1.5 }}
                                >
                                    {t('UsersPopup.TenantAdministration')}
                                </Button>
                                <Button
                                    size="small"
                                    variant={adminView === 'branding' ? 'contained' : 'outlined'}
                                    onClick={() => setAdminView('branding')}
                                    sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 1.5 }}
                                >
                                    {t('Branding.Title')}
                                </Button>
                            </Box>
                        )}
                        <Button
                            onClick={() => { navigate('/portal'); }}
                            sx={{ height: 40, minWidth: 116, px: 1.5, flexShrink: 0 }}
                        >
                            <Box className="close-button-div" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography color='primary' fontSize={16} lineHeight={1}>{t('Close')}</Typography>
                                <Close color='primary' />
                            </Box>
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Box className="flex-container admin-content" sx={{ flex: 1, minHeight: 0, mt: 1 }}>
                {isActualTenant && adminView === 'branding' && (
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        <OrganizationBranding />
                    </Box>
                )}
                {!(isActualTenant && adminView === 'branding') && <>
                {!isActualTenant && <Box className="flex-item">
                    <Box className="flex-container" style={{ marginTop: '10px', marginLeft: '25px' }}>
                        <AccountCircleOutlined color='primary' sx={{ width: "30px", height: "30px" }} />
                        <Typography color='primary' sx={{ margin: '4px', fontWeight: 'bolder', caretColor: 'transparent' }} >{t('Administration.TenantOverview')}</Typography>
                    </Box>
                    <Box sx={{ marginTop: '10px', ml: 2 }}>
                        <Button variant="outlined" startIcon={<AddCircle />} color='primary' onClick={() => { setUpdatingTenant(undefined); setTenantDialogOpen(true); }} sx={{ height: "30px", fontSize: '10px', fontWeight: 'bold', boxShadow: 3, caretColor: 'transparent' }}>
                            {t('Administration.AddTenant')}
                        </Button>
                    </Box>
                    {tenants && <Box className="admin-list" style={{ marginTop: '20px', marginLeft: '25px', marginRight: '10px' }}>
                        {tenants.map((tenant) => {
                            return <Box key={tenant.id}>
                                <Box onClick={() => { selectTenant(tenant) }} sx={{ display: 'flex', color: selectedTenant !== undefined && selectedTenant.id === tenant.id ? 'primary.main' : 'text.primary', borderBottom: '1px solid', borderColor: 'divider', backgroundColor: selectedTenant !== undefined && selectedTenant.id === tenant.id ? alpha(theme.palette.primary.main, 0.06) : 'transparent', transition: 'background-color 0.15s ease', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
                                    <Box style={{ lineHeight: '40px', margin: '0px 10px', caretColor: 'transparent', cursor: 'pointer' }}>{tenant.name}</Box>
                                    <Box style={{ display: 'flex', marginLeft: 'auto', marginRight: '0px' }}>
                                        <Tooltip title={t('Administration.PermissionSettings')}>
                                            <IconButton color='primary' onClick={() => {

                                                setTenantPermissionId(tenant.id ?? null);
                                            }}><SettingsOutlined /></IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('Administration.pricingDatabaseSharing')}>
                                            <IconButton color='primary' onClick={() => {
                                                setSharingTenant({ id: tenant.id ?? 0, name: tenant.name });
                                            }}><ShareOutlined /></IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('Administration.EditTenant')}>
                                            <IconButton color='primary' onClick={() => {
                                                setUpdatingTenant(tenant);
                                                setTenantDialogOpen(true);
                                            }}><EditOutlined /></IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('Administration.DeleteTenant')}>
                                            <IconButton color='primary' onClick={() => {
                                                setDeletingTenant(tenant);
                                            }}><DeleteOutline /></IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Box>;
                        })
                        }
                    </Box>
                    }
                </Box>
                }
                {selectedTenant && <Box className="flex-item">
                    <Box className="flex-container" style={{ marginTop: '10px', marginLeft: '25px' }}>
                        <People color='primary' sx={{ width: "30px", height: "30px" }} />
                        <Typography color='primary' sx={{ margin: '4px', fontWeight: 'bolder', caretColor: 'transparent' }} >{t('Administration.UsersOverview')}</Typography>
                    </Box>
                    <Box sx={{ marginTop: '10px', ml: 2 }}>
                        <Button disabled={!isTenantManager} variant="outlined" startIcon={<AddCircle />} color='primary' onClick={() => { setUserDialogOpen(true); }} sx={{ height: "30px", fontSize: '10px', fontWeight: 'bold', boxShadow: 3, caretColor: 'transparent' }} >
                            {t('Administration.AddUser')}
                        </Button>
                        <Button disabled={!isTenantManager} variant="outlined" startIcon={<AddCircleOutlineOutlined />} color='primary' onClick={() => { setImportUsersDialogOpen(true); }} sx={{ height: "30px", fontSize: '10px', fontWeight: 'bold', boxShadow: 3, caretColor: 'transparent', mx: 1 }} >
                            {t('Administration.ImportUsersExcel')}
                        </Button>
                    </Box>
                    <Box sx={{ ml: 3, mt: 1 }}>
                        <Link 
                            to="/import_user_example.xlsx" 
                            target="_blank" 
                            download 
                            style={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
                        >
                            {t('Administration.exampleExcelFile')}
                        </Link>
                    </Box>
                    {selectedTenant.users && <Box className="admin-list" style={{ marginTop: '10px', marginLeft: '25px', marginRight: '10px' }}>
                        {selectedTenant.users.map((user) => {
                            return <Box key={user.id} onClick={() => { setSelectedUser(user); }} sx={{ display: 'flex', color: selectedUser !== undefined && selectedUser.id === user.id ? 'primary.main' : 'text.primary', backgroundColor: selectedUser !== undefined && selectedUser.id === user.id ? alpha(theme.palette.primary.main, 0.06) : 'background.paper', borderBottom: '1px solid', borderColor: 'divider', marginTop: '6px', transition: 'background-color 0.15s ease', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
                                <Box className="circle">
                                    <p className="circle-inner">
                                        {user.name ? user.name.substring(0, 1) : ''}{user.surname ? user.surname.substring(0, 1) : ''}
                                    </p>
                                </Box>
                                <Box style={{ cursor: 'pointer', margin: '8px', alignContent: 'center', caretColor: 'transparent', borderBottom: '1px' }}>
                                    {user.name} {user.surname}
                                </Box>
                                {(isAdmin || (isActualTenant && isTenantManager && !user.isAdmin && user.id !== userData?.id)) && <Box style={{ display: 'flex', marginLeft: 'auto', marginRight: '0px' }}>
                                    <Tooltip title={t('Administration.DeleteUser')}>
                                        <IconButton color='primary' onClick={() => {
                                            setRemovingUser(user);
                                        }}><DeleteOutline /></IconButton>
                                    </Tooltip>
                                </Box>}
                            </Box>;
                        })
                        }
                    </Box>
                    }
                </Box>
                }
                {!selectedTenant && !isActualTenant && <Box style={{ margin: '64px', marginLeft: '96px' }}><NoDataPlaceholder title={t('Administration.SelectTenant')} paragraph='' /></Box>}
                {selectedTenant && selectedUser && <Box className="flex-item-no-border">
                    <Box className="circle-big">
                        <p className="circle-big-inner" >
                            {selectedUser.name ? selectedUser.name.substring(0, 1) : ''}{selectedUser.surname ? selectedUser.surname.substring(0, 1) : ''}
                        </p>
                    </Box>
                    <Box>
                        <Box className="flex-container" >
                            <Box>
                                <TextField sx={{ margin: '12px', marginLeft: '24px', width: '350px' }} label={t('Administration.FirstName')}
                                    disabled={!canEditSelectedUser}
                                    size='small'
                                    name='userName'
                                    variant='outlined'
                                    value={selectedUser.name}
                                    onChange={(e) => { setSelectedUser({ ...selectedUser, name: e.currentTarget.value }); }} />
                            </Box>
                            <Box>
                                <TextField
                                    sx={{ margin: '12px', width: '350px' }}
                                    label={t('Administration.LastName')}
                                    disabled={!canEditSelectedUser}
                                    size='small'
                                    variant='outlined'
                                    value={selectedUser.surname}
                                    onChange={(e) => { setSelectedUser({ ...selectedUser, surname: e.currentTarget.value }); }} />
                            </Box>
                        </Box>
                        <Box className="flex-container" >
                            <Box>
                                <TextField
                                    disabled
                                    sx={{ margin: '12px', marginLeft: '24px', width: '350px' }}
                                    label={t('Administration.Email')}
                                    size='small'
                                    variant='outlined'
                                    value={selectedUser.email}
                                    onChange={(e) => { setSelectedUser({ ...selectedUser, email: e.currentTarget.value }); }} />
                            </Box>
                            <Box>
                                <PermissionsSelectComponent
                                    tenantId={selectedTenant.id}
                                    disabled={!canEditSelectedUser}
                                    label={t('Administration.UsersRole')}
                                    selected={selectedUser.roleId}
                                    onSelect={(r) => {
                                        setSelectedUser({ ...selectedUser, roleId: r?.id });
                                    }}
                                     />
                            </Box>
                        </Box>
                        <Box m={0.5}>
                            <LanguagePicker showText notChangeLanguage externalLang={selectedUser.language} onLanguageChange={(lang) => {
                                setSelectedUser({ ...selectedUser, language: lang });
                            }} />
                        </Box>
                        <Box className="flex-container" >
                            <Box>
                                <FormGroup sx={{ margin: '12px', marginLeft: '24px' }}>
                                    <FormControlLabel disabled={!isAdmin} control={<Checkbox checked={selectedUser.isAdmin} onChange={(e) => { setSelectedUser({ ...selectedUser, isAdmin: e.target.checked }); }} />} label={t('UsersPopup.Admin')} />
                                </FormGroup>
                            </Box>
                            {selectedUser.identityProvider === 'Microsoft' && <Box style={{ marginLeft: '32px', marginTop: '18px' }}>
                                <Tooltip title={t('Administration.MicrosoftIdentityLogin')}>
                                    <Microsoft color='primary' />
                                </Tooltip>
                            </Box>}
                        </Box>
                        <Box className="flex-container" >
                            <Box>
                                <FormGroup sx={{ margin: '12px', marginLeft: '24px', width: '250px' }}>
                                    <FormControlLabel disabled={!canEditSelectedUser} control={<Checkbox checked={selectedUser.isTenantManager} onChange={(e) => { setSelectedUser({ ...selectedUser, isTenantManager: e.target.checked }); }} />} label={t('Administration.TenantManager')} />
                                </FormGroup>
                            </Box>
                        </Box>
                        {!isAdmin && !canEditSelectedUser && <Typography variant='caption' color='text.secondary' sx={{ display: 'block', marginLeft: '24px' }}>{t('Administration.AdminAccountHint')}</Typography>}
                        {!isAdmin && canEditSelectedUser && !canResetSelectedPassword && <Typography variant='caption' color='text.secondary' sx={{ display: 'block', marginLeft: '24px' }}>{t('Administration.SharedAccountHint')}</Typography>}
                        <Box className="flex-container" style={{ marginTop: '24px' }}>
                            <Box>
                                <Button variant='contained' sx={{ margin: 'auto', marginLeft: '24px' }} disabled={buttonDisabled || !canEditSelectedUser} onClick={saveUser}>{t('Save')} </Button>
                            </Box>
                            <Box>
                                <Button variant='contained' sx={{ margin: 'auto', marginLeft: '24px', minWidth: '150px' }} disabled={buttonResetDisabled || !canResetSelectedPassword} onClick={resetPassword}>{t('ResetPassword')} </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                }
                {selectedTenant && !selectedUser && <Box style={{ margin: '64px', marginLeft: '96px' }}><NoDataPlaceholder title={t('Administration.SelectUser')} paragraph='' /></Box>}
                </>}
                <Snackbar open={saveConfirm} autoHideDuration={3000} onClose={() => setSaveConfirm(false)}>
                    <Alert onClose={() => setSaveConfirm(false)} severity="success" sx={{ width: '100%' }}>
                        {t('Saved')}
                    </Alert>
                </Snackbar>
            </Box>
            <CreateTenantDialog tenant={updatingTenant} isOpen={tenantDialogOpen} onClose={(refresh, id) => {
                setTenantDialogOpen(false);
                if (refresh) {
                    refreshData(id, selectedUser ? selectedUser.id : undefined);
                }
            }} />
            <CreateUserDialog emailDef={emailCreate} tenantId={selectedTenant ? selectedTenant.id : undefined} isOpen={userCreateDialogOpen} onClose={(refresh) => {
                setUserCreateDialogOpen(false);
                setEmailCreate(undefined);
                if (refresh) {
                    refreshData(selectedTenant ? selectedTenant.id : undefined, selectedUser ? selectedUser.id : undefined);
                }
            }} />
            <AddUserToTenantDialog tenantId={selectedTenant ? selectedTenant.id : undefined} isOpen={userDialogOpen} onClose={(refresh, id, exists, email, already) => {
                setUserDialogOpen(false);
                if (refresh) {
                    setInfoProps({ label: t('Administration.TenantsAdding'), text: already ? t('Administration.UserIsAlreadyInTenant') : t('Administration.UserExistsNotInTenant') });
                    setInfoOpen(true);
                    refreshData(selectedTenant ? selectedTenant.id : undefined, id);
                }
                else if (exists === false) {
                    setEmailCreate(email);
                    setUserCreateDialogOpen(true);
                }
            }} />
            <InfoDialog label={infoProps ? infoProps.label : ''} onClose={() => setInfoOpen(false)} open={infoOpen} text={infoProps ? infoProps.text : ''} />
            <NewPasswordDialog isOpen={newPassDialogOpen} onClose={() => { setNewPass(''); setNewPassDialogOpen(false); }} newPassword={newPass} />
            <DeleteDialog open={deletingTenant !== null} onClose={() => { setDeletingTenant(null) }} data={deletingTenant} onDeleteAgree={
                (data) => {
                    if (data.id)
                        authorizedApi?.deleteTenant(data.id).then((res) => {
                            if (res.ok) {
                                setDeletingTenant(null);
                                refreshData(selectedTenant ? selectedTenant.id : undefined, selectedUser ? selectedUser.id : undefined);
                            }
                        });
                }
            } />
            <DeleteDialog open={removingUser !== null} onClose={() => { setRemovingUser(null) }} data={removingUser} onDeleteAgree={
                (data) => {
                    if (data.id && selectedTenant?.id)
                        authorizedApi?.removeUserFromTenant(new RemoveUserFromTenantRequest({
                            userId: data.id,
                            tenantId: selectedTenant.id
                        })).then((res) => {
                            if (res.ok) {
                                setRemovingUser(null);
                                refreshData(selectedTenant ? selectedTenant.id : undefined, selectedUser ? selectedUser.id : undefined);
                            }
                        });
                }
            } />
            <ImportUsersExcelDialog tenantId={selectedTenant?.id} isOpen={importUsersDialogOpen} onClose={() => {
                refreshData(selectedTenant ? selectedTenant.id : undefined, selectedUser ? selectedUser.id : undefined);
                setImportUsersDialogOpen(false);
            }} />
        </Box>
    )
}
